import { useEffect, useState } from 'react';

import {
  faLongArrowUp,
  faMoneyBillWave,
  faUmbrellaBeach
} from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Log } from './types';

import { getLocalizedMonthName } from 'utils/date';
import { firstLetterToUpperCase } from 'utils/misc';

import {
  creditTransaction,
  getData,
  getTransactions,
  setTransaction
} from 'RestAPIs/work-agreements/balance-budget';

import { showSnackbar } from 'components/shared';

import BalanceBudgetStyles from './BalanceBudgetStyles';
import LogEntry from './LogEntry';
import SourceCard from './SourceCard';
import TotalBudgetHours from './TotalBudgetHours';

type Props = {
  isEditMode: boolean;
  waId: number;
};

export default function BalanceBudget({ isEditMode, waId }: Props) {
  const { t } = useTranslation('common');
  const [hasData, setHasData] = useState<null | boolean>(null);
  const [year, setYear] = useState(0);
  const [month, setMonth] = useState(0);
  const [nonTruncatedExtraStatutoryLeave, setNonTruncatedExtraStatutoryLeave] =
    useState<number>(0);
  const [nonTruncatedPlb, setNonTruncatedPlb] = useState<number>(0);
  const [extraStatutoryLeave, setExtraStatutoryLeave] = useState(0);
  const [plbEnabled, setPlbEnabled] = useState(false);
  const [plb, setPlb] = useState(0);
  const [plbName, setPlbName] = useState('PLB');
  const [plusHours, setPlusHours] = useState(0);

  const [extraStatutoryLeavePreview, setExtraStatutoryLeavePreview] =
    useState(0);
  const [plbPreview, setPlbPreview] = useState(0);
  const [plusHoursPreview, setPlusHoursPreview] = useState(0);
  const [manualPreview, setManualPreview] = useState(0);
  const [savedInSDB, setSavedInSDB] = useState(0);
  const [savedInSalarySystem, setSavedInSalarySystem] = useState(0);
  const [savedInSalarySystemPreview, setSavedInSalarySystemPreview] =
    useState(0);
  const [logs, setLogs] = useState<Log[]>([]);
  const [maxHours, setMaxHours] = useState(3600);
  const savedInSDBPreview =
    extraStatutoryLeavePreview + plbPreview + plusHoursPreview + manualPreview;

  const total = savedInSDB + savedInSalarySystem;
  const totalPreview = savedInSDB + savedInSalarySystem + savedInSDBPreview;

  const tDiff = totalPreview / 100 - Math.trunc(totalPreview / 100);
  const currentMaxHours = Math.min(
    maxHours,
    Math.ceil((totalPreview + (tDiff > 0.8 || tDiff === 0 ? 100 : 0)) / 100) *
      100
  );
  const [remainingHours, setRemainingHours] = useState(0);

  useEffect(() => {
    refreshTransactions(waId);
  }, [waId]);

  const { refetch } = useQuery(
    ['BalanceBudget', waId],
    async () => {
      const { data } = await getData(waId);
      if (data) {
        const {
          year,
          month,
          availableExtraStatutoryLeaveHours,
          availablePlbHours,
          availablePlusHours,
          maxAllowedHours,
          planningHours,
          salaryHours,
          plbName
        } = data;
        setYear(year);
        setMonth(month);
        setPlbName(plbName);
        setExtraStatutoryLeave(
          parseFloat(availableExtraStatutoryLeaveHours.toFixed(2))
        );
        setNonTruncatedExtraStatutoryLeave(availableExtraStatutoryLeaveHours);
        const enabled = availablePlbHours != null;
        setPlbEnabled(enabled);
        if (enabled) {
          setPlb(parseFloat(availablePlbHours.toFixed(2)));
          setNonTruncatedPlb(availablePlbHours);
        }
        setPlusHours(parseFloat(availablePlusHours.toFixed(2)));
        setMaxHours(parseFloat(maxAllowedHours.toFixed(2)));
        setSavedInSDB(parseFloat(planningHours.toFixed(2)));
        setSavedInSalarySystem(parseFloat(salaryHours.toFixed(2)));
        setExtraStatutoryLeavePreview(0);
        setPlbPreview(0);
        setPlusHoursPreview(0);
        setManualPreview(0);
        setSavedInSalarySystemPreview(0);
      }
      setHasData(Boolean(data));
    },
    {
      enabled: !!waId
    }
  );

  const refreshTransactions = (waId) => {
    getTransactions(waId).then(({ data }) => {
      setLogs(
        data.map((log) => ({
          ...log,
          type: log.type,
          hours: parseFloat(log.hours.toFixed(2))
        }))
      );
    });
  };

  const refreshData = async (waId, onComplete) => {
    await refetch();

    if (onComplete) {
      onComplete();
    }

    refreshTransactions(waId);
  };

  const onExtraStatutoryLeaveSave = (value: number) => {
    const prevValue = extraStatutoryLeave;
    const hours =
      Math.abs(nonTruncatedExtraStatutoryLeave - value) <= 0.1
        ? nonTruncatedExtraStatutoryLeave
        : value;
    setTransaction(waId, { type: 'EXTRA_STATUTORY_LEAVE', hours }).then(
      () => {
        refreshData(waId, () => {
          setExtraStatutoryLeave(parseFloat((prevValue - value).toFixed(2)));
        });
      },
      () => {
        setExtraStatutoryLeavePreview(0);
        setExtraStatutoryLeave(prevValue);
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    );
  };

  const onPlbSave = (value: number) => {
    const prevValue = plb;
    const hours =
      Math.abs(nonTruncatedPlb - value) <= 0.1 ? nonTruncatedPlb : value;
    setTransaction(waId, { type: 'PLB', hours }).then(
      () => {
        refreshData(waId, () => {
          setPlb(parseFloat((prevValue - value).toFixed(2)));
        });
      },
      () => {
        setPlbPreview(0);
        setPlb(prevValue);
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    );
  };

  const onPlusHoursSave = (value: number) => {
    const prevValue = plusHours;
    setTransaction(waId, { type: 'PLUS_HOURS', hours: value }).then(
      () => {
        refreshData(waId, () => {
          setPlusHours(parseFloat((prevValue - value).toFixed(2)));
        });
      },
      () => {
        setPlusHoursPreview(0);
        setPlusHours(prevValue);
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    );
  };

  const onManualSave = (value: number) => {
    setTransaction(waId, { type: 'MANUAL', hours: value }).then(
      () => {
        refreshData(waId, null);
      },
      () => {
        setManualPreview(0);
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    );
  };

  const onSalaryExportSave = (value: number) => {
    setTransaction(waId, { type: 'SALARY_EXPORT', hours: value }).then(
      () => {
        refreshData(waId, null);
      },
      () => {
        setSavedInSalarySystemPreview(0);
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    );
  };

  const onCreditTransaction = (log: Log) => {
    const prevExtraStatutoryLeave = extraStatutoryLeave;
    const prevPlusHours = plusHours;
    const prevPlbHours = plb;
    creditTransaction(waId, log.id).then(
      () => {
        refreshData(waId, () => {
          if (log.type === 'EXTRA_STATUTORY_LEAVE') {
            setExtraStatutoryLeave(
              parseFloat((prevExtraStatutoryLeave + log.hours).toFixed(2))
            );
          } else if (log.type === 'PLUS_HOURS') {
            setPlusHours(parseFloat((prevPlusHours + log.hours).toFixed(2)));
          } else if (log.type === 'PLB') {
            setPlb(parseFloat((prevPlbHours + log.hours).toFixed(2)));
          }
        });
      },
      () => {
        setSavedInSalarySystemPreview(0);
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    );
  };

  useEffect(() => {
    setRemainingHours(maxHours - savedInSDB - savedInSalarySystem);
  }, [maxHours, savedInSDB, savedInSalarySystem]);

  return (
    <>
      {hasData && (
        <BalanceBudgetStyles>
          <div className="side left-side">
            <h3>{t('balanceBudget.savingSources')}</h3>

            <SourceCard
              type="slider"
              icon={faUmbrellaBeach}
              description={t('balanceBudget.cardSource1', { year })}
              sliderMax={extraStatutoryLeave}
              maxHours={maxHours - total}
              readonly={!isEditMode}
              remainingHours={remainingHours}
              onChange={(value) => setExtraStatutoryLeavePreview(value)}
              onSave={onExtraStatutoryLeaveSave}
            />
            {plbEnabled && (
              <SourceCard
                type="slider"
                icon={faUmbrellaBeach}
                description={t('balanceBudget.cardPlb', { year, plbName })}
                sliderMax={plb}
                maxHours={maxHours - total}
                readonly={!isEditMode}
                remainingHours={remainingHours}
                onChange={(value) => setPlbPreview(value)}
                onSave={onPlbSave}
              />
            )}

            <SourceCard
              type="slider"
              icon={faLongArrowUp}
              description={t('balanceBudget.cardSource2', {
                month: `${firstLetterToUpperCase(
                  getLocalizedMonthName(month - 1)
                )} ${year}`
              })}
              sliderMax={plusHours}
              maxHours={maxHours - total}
              remainingHours={remainingHours}
              readonly={!isEditMode}
              onChange={(value) => setPlusHoursPreview(value)}
              onSave={onPlusHoursSave}
            />

            {isEditMode && (
              <>
                <SourceCard
                  type="number"
                  icon={faMoneyBillWave}
                  description={t('balanceBudget.cardSource3')}
                  maxHours={maxHours - total}
                  remainingHours={remainingHours}
                  onChange={(value) => setManualPreview(value)}
                  onSave={onManualSave}
                />

                <SourceCard
                  type="export"
                  icon={faMoneyBillWave}
                  description={t('balanceBudget.cardExport')}
                  sliderMax={savedInSDB}
                  maxHours={savedInSDB}
                  remainingHours={remainingHours}
                  onChange={(value) => setSavedInSalarySystemPreview(value)}
                  onSave={onSalaryExportSave}
                  className="salary-system-card"
                />
              </>
            )}
          </div>

          <div className="divider" />

          <div className="side right-side">
            <h3>{t('overview')}</h3>
            <TotalBudgetHours
              savedInSDB={savedInSDB}
              savedInSDBPreview={savedInSDBPreview}
              savedInSalarySystem={savedInSalarySystem}
              savedInSalarySystemPreview={savedInSalarySystemPreview}
              currentMaxHours={currentMaxHours}
              maxHours={maxHours}
              remainingHours={remainingHours}
            />

            {logs.length > 0 && (
              <div className="logs">
                <h3>{t('logs')}</h3>
                {logs.map((log) => (
                  <LogEntry
                    key={log.id}
                    isEditMode={isEditMode}
                    log={log}
                    onCredit={(log) => onCreditTransaction(log)}
                  />
                ))}
              </div>
            )}
          </div>
        </BalanceBudgetStyles>
      )}
      {hasData === false && (
        <h3>{t('balanceBudget.notAllowedForThisContract')}</h3>
      )}
    </>
  );
}
